import React from 'react';
import { Link } from 'react-router-dom';
import EmailCapture from './EmailCapture';
import { plans, lowestPaidPrice } from './plans';
import './App.css';

const apps = [
  {
    id: 'macos',
    title: 'mChatAI+',
    platform: 'macOS',
    description: 'AI automation platform for your Mac. Chat with any LLM, build pipelines, run autonomous agents, manage a personal feed, and orchestrate developer workflows — all from one native app.',
    features: ['Multi-provider LLM chat', 'Pipeline automation engine', 'Autonomous AI agents', 'AI Feed & Vault', 'Mini-app platform'],
    link: '/macos-app',
    cta: 'Learn more',
    accent: '#3b82f6',
    iconBg: '#eff6ff',
  },
  {
    id: 'ios',
    title: 'mChatAI',
    platform: 'iOS',
    description: 'Your AI companion on the go. Chat, write, manage tasks, and monitor your Mac automations from your iPhone. Syncs with mChatAI+ via iCloud.',
    features: ['Multi-provider chat', 'AI writing assistant', 'ToDo with AI', 'Remote agent monitoring', 'HealthKit & MapGuide'],
    link: '/ios-app',
    cta: 'Get the app',
    accent: '#8b5cf6',
    iconBg: '#f5f3ff',
  },
  {
    id: 'android',
    title: 'mChatAI',
    platform: 'Android',
    description: 'AI chat, audio and a hub of community-built mini-apps and games on your phone. Free models need no account, so you can try the whole thing before signing in.',
    features: ['Multi-provider chat', 'Community Hub mini-apps', 'Audio library & podcasts', 'Radio, news & reader', 'Map Guide walking tours'],
    link: '/android-app',
    cta: 'Get it on Google Play',
    accent: '#22c55e',
    iconBg: '#f0fdf4',
  },
  {
    id: 'shell',
    title: 'mChatAIShell',
    platform: 'Menu bar',
    description: 'A lightweight menu-bar companion that turns your Mac into an AI execution engine. Run dev sessions with Claude Code, Gemini, or Codex. Host Python microservices. Execute skills and probes.',
    features: ['6 dev session backends', 'Python microservices', 'Capability probes', 'Community extensions', '40+ HTTP endpoints'],
    link: '/shell-app',
    cta: 'Download',
    accent: '#10b981',
    iconBg: '#ecfdf5',
  },
];

export default function HomePage() {
  return (
    <div className="mp-page" style={{ background: '#fff' }}>
      {/* Hero */}
      <section style={{ textAlign: 'center', padding: '80px 24px 48px', maxWidth: 640, margin: '0 auto' }}>
        <h1 style={{ fontSize: '2.8rem', fontWeight: 800, color: '#111827', margin: '0 0 16px', lineHeight: 1.1 }}>
          mChatAI
        </h1>
        <p style={{ fontSize: '1.15rem', color: '#6b7280', lineHeight: 1.7, margin: 0 }}>
          A native AI platform for macOS, iOS and Android. Chat with any model, automate with
          pipelines, deploy autonomous agents, and extend with community-built skills.
        </p>
        {/* Prices used to be findable only from the top bar (2026-10-15). */}
        <p style={{ fontSize: '0.9rem', color: '#9ca3af', margin: '16px 0 0' }}>
          Free with your own keys and local models · plans from {lowestPaidPrice}/month ·{' '}
          <Link to="/pricing" style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: 600 }}>See pricing</Link>
        </p>
      </section>

      {/* App Cards */}
      <section style={{ maxWidth: 960, margin: '0 auto 64px', padding: '0 24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
          {apps.map((app) => (
            <Link key={app.id} to={app.link} className="mp-card-link">
              <article style={{
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: 14,
                padding: 28,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'border-color 0.15s, box-shadow 0.15s, transform 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = app.accent; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.06)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}
              >
                <div style={{
                  width: 44, height: 44, borderRadius: 12,
                  background: app.iconBg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: 14,
                }}>
                  <span style={{ fontSize: '1.2rem', fontWeight: 700, color: app.accent }}>
                    {app.title[0]}
                  </span>
                </div>
                <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9ca3af', marginBottom: 4 }}>
                  {app.platform}
                </div>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', margin: '0 0 10px' }}>
                  {app.title}
                </h2>
                <p style={{ fontSize: '0.88rem', color: '#6b7280', lineHeight: 1.6, margin: '0 0 16px', flex: 1 }}>
                  {app.description}
                </p>
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 16px' }}>
                  {app.features.map((f) => (
                    <li key={f} style={{ fontSize: '0.82rem', color: '#374151', padding: '3px 0', paddingLeft: 16, position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 0, color: app.accent }}>+</span>
                      {f}
                    </li>
                  ))}
                </ul>
                <span style={{
                  display: 'inline-block', padding: '8px 20px', fontSize: '0.85rem',
                  background: app.accent, color: '#fff', borderRadius: 8, fontWeight: 600,
                }}>
                  {app.cta}
                </span>
              </article>
            </Link>
          ))}
        </div>
      </section>

      {/* Extensions callout */}
      <section style={{ maxWidth: 560, margin: '0 auto 64px', textAlign: 'center', padding: '0 24px' }}>
        <h2 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#111827', marginBottom: 8 }}>
          Extend with community content
        </h2>
        <p style={{ color: '#6b7280', lineHeight: 1.6, marginBottom: 20 }}>
          Browse skills, agents, games, and templates built by the community — or publish your own.
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
          <Link to="/hub" style={{
            display: 'inline-block', padding: '10px 24px',
            background: '#111827', color: '#fff', borderRadius: 8, fontWeight: 600,
            textDecoration: 'none', fontSize: '0.9rem',
          }}>Hub</Link>
        </div>
      </section>

      {/* Security section */}
      <section style={{ maxWidth: 800, margin: '0 auto 64px', padding: '0 24px' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <h2 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#111827', marginBottom: 8 }}>
            Your agents, on your Mac
          </h2>
          <p style={{ color: '#6b7280', lineHeight: 1.6, maxWidth: 550, margin: '0 auto' }}>
            mChatAI+ runs agents and tools on your own Mac, with the AI providers you choose or with local models.
          </p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
          {[
            { icon: '💻', title: 'Runs on your Mac', desc: 'Agents, pipelines and tools run on your machine, with the folders and accounts you give them.', color: '#3b82f6', bg: '#eff6ff' },
            { icon: '🔑', title: 'Keys in your Keychain', desc: 'Your API keys are stored in the macOS Keychain on your Mac.', color: '#10b981', bg: '#ecfdf5' },
            { icon: '🧠', title: 'Local models', desc: "Use Ollama or Apple's on-device model when you'd rather keep a task off the cloud.", color: '#f59e0b', bg: '#fffbeb' },
            { icon: '🛡️', title: 'Optional agent sandbox', desc: 'Dev sessions can run in NVIDIA OpenShell sandboxes: network blocked by default, API keys kept outside the sandbox, allowed and blocked connections logged. Needs mChatAIShell, an Apple Silicon Mac and an API key.', color: '#8b5cf6', bg: '#f5f3ff' },
            { icon: '📱', title: 'Check in from your iPhone', desc: 'Pair the iPhone app with your Mac to follow its agent sessions.', color: '#06b6d4', bg: '#ecfeff' },
          ].map((item) => (
            <div key={item.title} style={{
              background: '#fff', border: '1px solid #e5e7eb',
              borderRadius: 12, padding: 20, textAlign: 'left',
              transition: 'border-color 0.15s',
            }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10, background: item.bg,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '1.1rem', marginBottom: 10,
              }}>{item.icon}</div>
              <h3 style={{ fontSize: '0.95rem', fontWeight: 600, color: '#111827', margin: '0 0 6px' }}>{item.title}</h3>
              <p style={{ fontSize: '0.82rem', color: '#6b7280', lineHeight: 1.5, margin: 0 }}>{item.desc}</p>
            </div>
          ))}
        </div>
        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <p style={{ fontSize: '0.82rem', color: '#9ca3af' }}>
            The agent sandbox uses <a href="https://github.com/NVIDIA/OpenShell" style={{ color: '#3b82f6', textDecoration: 'none' }}>NVIDIA OpenShell</a>, an open-source agent runtime.
          </p>
        </div>
      </section>

      {/* Plans — the same list /pricing draws (src/plans.js), one line each. */}
      <section id="plans" style={{ maxWidth: 960, margin: '0 auto 64px', padding: '0 24px' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <h2 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#111827', marginBottom: 8 }}>
            Free to start. Pay only for the cloud.
          </h2>
          <p style={{ color: '#6b7280', lineHeight: 1.6, maxWidth: 560, margin: '0 auto' }}>
            Everything that runs on your devices is free. A plan adds the cloud features that cost us money to run.
          </p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
          {plans.map((plan) => (
            <Link key={plan.id} to="/pricing" className="mp-card-link">
              <div style={{
                background: plan.highlight ? '#eff6ff' : '#fff',
                border: `1px solid ${plan.highlight ? '#3b82f6' : '#e5e7eb'}`,
                borderRadius: 12, padding: 20, height: '100%', boxSizing: 'border-box',
              }}>
                <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>{plan.name}</div>
                <div style={{ marginBottom: 10 }}>
                  <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827' }}>{plan.price}</span>
                  <span style={{ fontSize: '0.85rem', color: '#9ca3af' }}>{plan.period}</span>
                </div>
                <p style={{ fontSize: '0.85rem', color: '#4b5563', lineHeight: 1.5, margin: 0 }}>{plan.summary}</p>
              </div>
            </Link>
          ))}
        </div>
        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <Link to="/pricing" style={{
            display: 'inline-block', padding: '10px 24px',
            background: '#111827', color: '#fff', borderRadius: 8, fontWeight: 600,
            textDecoration: 'none', fontSize: '0.9rem',
          }}>Compare plans</Link>
        </div>
      </section>

      {/* Newsletter capture — sits just above the footer so it gets the
          "you read the whole page" placement. Phase MM-2 follow-up. */}
      <section style={{ maxWidth: 720, margin: '0 auto', padding: '48px 24px 32px' }}>
        <EmailCapture
          source="homepage-footer"
          title="One short email each Sunday"
          subtitle="A new recipe, a community build, and what we're shipping next. No noise."
        />
      </section>

      {/* Footer */}
      <footer style={{ textAlign: 'center', padding: '32px 24px', borderTop: '1px solid #e5e7eb' }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginBottom: 8 }}>
          <Link to="/pricing" style={{ color: '#9ca3af', fontSize: '0.82rem', textDecoration: 'none' }}>Pricing</Link>
          <Link to="/docs" style={{ color: '#9ca3af', fontSize: '0.82rem', textDecoration: 'none' }}>Documentation</Link>
          <Link to="/privacy-policy" style={{ color: '#9ca3af', fontSize: '0.82rem', textDecoration: 'none' }}>Privacy Policy</Link>
          <Link to="/credits" style={{ color: '#9ca3af', fontSize: '0.82rem', textDecoration: 'none' }}>Credits</Link>
        </div>
        <p style={{ color: '#d1d5db', fontSize: '0.75rem', marginTop: 8 }}>
          &copy; {new Date().getFullYear()} mChatAI. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
